import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  VerificationMethod,
  VerificationSource,
  readVerifyCodeAnswer,
} from '../../models/verification.model';
import { SpentAuthenticatorCodesService } from '../../services/spent-authenticator-codes.service';
import { VerificationService } from '../../services/verification.service';
import * as VerificationActions from './verification.actions';
import {
  selectIsLiveVerificationChallenge,
  selectVerificationAttemptsRemaining,
  selectVerificationMethod,
} from './verification.selectors';

/**
 * Single effect that owns the `POST /verification/verify` call.
 *
 * Each feature dispatches `submitVerificationCode` and reacts to the
 * resulting `submitVerificationCodeSuccess` (checked against the `source`
 * and `actionId` it raised) to fire its confirmation step. Pure transport
 * plumbing lives here.
 *
 * An answer is only ever about the challenge the code was typed for, and only
 * while the operator is still answering it: a code for a challenge the slice
 * no longer holds is not sent, and an answer that lands after the challenge
 * was cleared or replaced is dropped. Leaving the screen means no (F-053);
 * one flow's handle is never announced to another (F-055).
 */
@Injectable()
export class VerificationEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly verificationService = inject(VerificationService);
  private readonly translate = inject(TranslateService);
  private readonly spentCodes = inject(SpentAuthenticatorCodesService);

  submitVerificationCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VerificationActions.submitVerificationCode),
      mergeMap(({ actionId, code, source }) =>
        this.isLive(actionId, source).pipe(
          take(1),
          filter(Boolean),
          withLatestFrom(
            this.store.select(selectVerificationMethod),
            this.store.select(selectVerificationAttemptsRemaining)
          ),
          mergeMap(([, method, attemptsRemaining]) => {
            const isAuthenticator = method === VerificationMethod.GAUTH;
            // An authenticator code this tab already approved something with
            // would be refused as reused, at the cost of a try: say so here,
            // unsent.
            if (isAuthenticator && this.spentCodes.isSpent(code)) {
              return of(this.codeReusedFailure(attemptsRemaining));
            }
            return this.verify(actionId, code, source).pipe(
              tap(answer => {
                if (
                  isAuthenticator &&
                  answer.type ===
                    VerificationActions.submitVerificationCodeSuccess.type
                ) {
                  this.spentCodes.remember(code);
                }
              })
            );
          })
        )
      )
    )
  );

  private codeReusedFailure(attemptsRemaining: number | null) {
    return VerificationActions.submitVerificationCodeFailure({
      error: this.translate.instant('verification.errors.codeReused'),
      attemptsRemaining,
      codeReused: true,
    });
  }

  private isLive(
    actionId: string,
    source: VerificationSource
  ): Observable<boolean> {
    return this.store.select(
      selectIsLiveVerificationChallenge(actionId, source)
    );
  }

  private verify(actionId: string, code: string, source: VerificationSource) {
    return this.verificationService.verifyCode(actionId, code).pipe(
      map(response => {
        // verification-management answers with a bare body, not an envelope
        // (an enveloped one is read too). The only affirmative answer is
        // `verified: true`; a missing, null or non-boolean verdict is a code
        // not verified (F-054).
        const data = readVerifyCodeAnswer(response);
        if (data?.verified !== true) {
          const remainingRaw = data?.attemptsRemaining;
          const remaining =
            typeof remainingRaw === 'number' ? remainingRaw : null;
          return VerificationActions.submitVerificationCodeFailure({
            error:
              remaining !== null && remaining > 0
                ? this.translate.instant(
                    'verification.errors.verifyWrongAttempts',
                    { count: remaining }
                  )
                : this.translate.instant('verification.errors.verifyWrong'),
            attemptsRemaining: remaining,
          });
        }
        return VerificationActions.submitVerificationCodeSuccess({
          actionId,
          source,
        });
      }),
      catchError((err: unknown) => {
        const anyErr = err as {
          error?: {
            errorCode?: string;
            message?: string | { message?: string; attemptsRemaining?: number };
            attemptsRemaining?: number;
            data?: { attemptsRemaining?: number };
          };
        };
        const payload = anyErr?.error ?? {};
        // verification-management nests `{ message, attemptsRemaining }`
        // under `message` for a refused code; older shapes put the count
        // at the top level or under `data`. Read all three.
        const nested =
          payload?.message && typeof payload.message === 'object'
            ? payload.message
            : null;
        const remainingRaw =
          payload?.attemptsRemaining ??
          payload?.data?.attemptsRemaining ??
          nested?.attemptsRemaining;
        const attemptsRemaining =
          typeof remainingRaw === 'number' ? remainingRaw : null;

        // The handle is dead: no code typed against it will ever be
        // accepted again. Say so, and let the modal offer a restart.
        if (payload?.errorCode === 'VERIFICATION_LOCKED') {
          return of(
            VerificationActions.submitVerificationCodeFailure({
              error: this.translate.instant('verification.errors.verifyLocked'),
              attemptsRemaining: 0,
              locked: true,
            })
          );
        }
        // A right authenticator code another approval already spent: one
        // code approves one action, so the operator waits for the next one.
        if (payload?.errorCode === 'VERIFICATION_CODE_REUSED') {
          return of(this.codeReusedFailure(attemptsRemaining));
        }
        // The platform cannot use this method right now (e.g. it cannot
        // read its own copy of a TOTP secret): not the operator's fault,
        // and not a spent attempt.
        // Too many submissions in the window (verification-management
        // F-019): not a wrong code and not a spent attempt — wait and retry.
        if (payload?.errorCode === 'VERIFICATION_RATE_LIMITED') {
          const retryAfterRaw = (
            nested as { retryAfterSeconds?: number } | null
          )?.retryAfterSeconds;
          const minutes = Math.max(
            1,
            Math.ceil(
              (typeof retryAfterRaw === 'number' ? retryAfterRaw : 900) / 60
            )
          );
          return of(
            VerificationActions.submitVerificationCodeFailure({
              error: this.translate.instant('verification.errors.rateLimited', {
                minutes,
              }),
              attemptsRemaining,
            })
          );
        }
        // The rate limiter itself could not be asked; the submission was
        // refused unread. Transient, and not the operator's fault.
        if (payload?.errorCode === 'RATE_LIMIT_UNAVAILABLE') {
          return of(
            VerificationActions.submitVerificationCodeFailure({
              error: this.translate.instant(
                'verification.errors.temporarilyUnavailable'
              ),
              attemptsRemaining,
            })
          );
        }
        if (payload?.errorCode === 'VERIFICATION_METHOD_UNAVAILABLE') {
          return of(
            VerificationActions.submitVerificationCodeFailure({
              error: this.translate.instant(
                'verification.errors.methodUnavailable'
              ),
              attemptsRemaining,
            })
          );
        }

        // A top-level sentence is already in the operator's language and
        // is shown verbatim. The nested shape is verification-management's
        // own, whose sentence is not localized — so for it the count is
        // said in the operator's language instead, as the 200 path does.
        let message: string;
        if (typeof payload?.message === 'string' && payload.message.trim()) {
          message = payload.message;
        } else if (attemptsRemaining !== null && attemptsRemaining > 0) {
          message = this.translate.instant(
            'verification.errors.verifyWrongAttempts',
            { count: attemptsRemaining }
          );
        } else if (
          typeof nested?.message === 'string' &&
          nested.message.trim()
        ) {
          message = nested.message;
        } else {
          message = this.translate.instant('verification.errors.verifyFailed');
        }
        return of(
          VerificationActions.submitVerificationCodeFailure({
            error: message,
            attemptsRemaining,
          })
        );
      }),
      // The operator may have left the screen (or another flow taken the
      // slice) while the code was on the wire: the answer is then about a
      // challenge nobody is answering any more, and is dropped.
      withLatestFrom(this.isLive(actionId, source)),
      filter(([, live]) => live),
      map(([answer]) => answer)
    );
  }
}
